// src/data/factsActs.js
//
// Record 605 (ruling 138) - CP-3: THE CLAIMANT'S CHANGE OR WITHDRAWAL OF HER FILED FACTS, AS A RECORDED ACT.
//
// Ruling 138 (data\YCP_RULINGS_2026-09-11.md, heading at line 43): "THE FACTS ARE REVOCABLE BY THE CLAIMANT; A CHANGE
// OR WITHDRAWAL IS A DATED, VISIBLE ACT, NEVER SILENT AND NEVER DESTRUCTIVE; THE ORGANIZATION IS INFORMED IMMEDIATELY".
// Audit spec: outputs\audit\AUDIT_RULINGS_111-170.md, BROKEN item 4 (FIX 4 (b) and (c)). Record 593 already put the
// fact fields in the history whitelist; this module is the act itself.
//
// PURE. No firebase import, no clock, no network: the caller passes the claim, the caller and the time, and gets back
// data to write. scripts\sync-shared-modules.js projects this file into functions\shared\factsActs.js, so the Cloud
// Function `factsAct` (functions\index.js) and the claim page decide with ONE definition (directives XI).
//
// WHAT AN ACT IS (claims/{id}/factsActs/{auto}, written ONLY by the factsAct function - firestore.rules refuses every
// client write):
//   { kind, field, before, after, at, byUid, reason }
//     kind     'changed'   - a listed fact field (or the Statement of Claim) gets new text
//              'withdrawn' - she withdraws it. NEVER A DELETE: the text stays on the claim exactly as it was, and the
//                            claim's `factsWithdrawn.<field>` marks it withdrawn (one marker map, one place).
//     before   the value as it stood before the act (null when there was none)
//     after    the value after the act - for a withdrawal, the same value, kept
//     at       the server's clock (the function writes serverTimestamp; `at` here is only the ISO used in notices)
//
// WHO MAY ACT - factsClaimant(). The stricter of two tests (the brief: "use the stricter of the two"):
//   carriageAct's  claim.claimantUid === the caller's uid (functions\index.js exports.carriageAct)
//   the rules'     isClaimOfSelf(): the creator, OR claimantUid, OR the claimant e-mail (firestore.rules, section 517)
// So: where the claim names its claimant's account (claimantUid), only that account. Where it does not, only the
// creator who filed it for HIMSELF - a member or tester account (not an officer filing for somebody), and only where
// the claim's claimant e-mail is blank or is his own. An e-mail match alone is NOT enough (the e-mail on a claim is
// typed by whoever filed it); that claimant is told the claim must be linked to her account first.

export const CHANGED = 'changed';
export const WITHDRAWN = 'withdrawn';
export const FACTS_ACT_KINDS = [CHANGED, WITHDRAWN];

// The claimant's alleged facts (the fields firestore.rules factsIntact() pins against officers, Record 593) and her
// Statement of Claim. incidentTime24 is not listed: it is the section-537 officer completion field, not her allegation.
export const FACT_FIELDS = [
  'statementOfClaim',
  'description', 'descriptionAsWritten', 'approvedNarrative', 'box1Answers', 'box3Answers',
  'incidentDate', 'personsInvolved', 'witnesses', 'detailsOfWork', 'reasonsSupporting',
];

export const FIELD_LABELS = {
  statementOfClaim: 'the Statement of Claim',
  description: 'her description of what happened',
  descriptionAsWritten: 'her description as she first wrote it',
  approvedNarrative: 'the approved narrative',
  box1Answers: 'her answers to the Box 1 questions',
  box3Answers: 'her answers to the Box 3 questions',
  incidentDate: 'the date of the violation',
  personsInvolved: 'the persons involved',
  witnesses: 'the witnesses',
  detailsOfWork: 'the details of the work',
  reasonsSupporting: 'the reasons supporting the claim',
};

// The two notification kinds this act raises (src\data\notifications.js shape; NotificationCentre reads any kind).
export const NOTICE_KINDS = { changed: 'facts_changed', withdrawn: 'facts_withdrawn' };

export const TEXT_MAX = 20000;
export const REASON_MAX = 2000;
const OFFICER_ROLES = ['local_chair', 'general_chair', 'admin'];
const SELF_FILER_ROLES = ['member', 'tester'];

const s = (v) => (v == null ? '' : String(v).trim());

// The text a field holds, for comparison and display. The Statement of Claim is { text, approvedBy, ... }.
export function factText(claim, field) {
  const v = (claim || {})[field];
  if (v == null) return '';
  if (field === 'statementOfClaim') return s(v && v.text);
  if (typeof v === 'string') return v.trim();
  return '';
}

export function isWithdrawn(claim, field) {
  const m = (claim || {}).factsWithdrawn;
  return !!(m && typeof m === 'object' && m[field]);
}

// WHO MAY ACT. caller = { uid, role, email }. Returns { may, because }.
export function factsClaimant(claim, caller) {
  const c = claim || {};
  const k = caller || {};
  const uid = s(k.uid);
  if (!uid) return { may: false, because: 'Sign in required.' };
  const claimantUid = s(c.claimantUid);
  if (claimantUid) {
    return claimantUid === uid
      ? { may: true, because: '' }
      : { may: false, because: 'Only the claimant may change or withdraw the facts of her claim.' };
  }
  if (s(c.createdBy) !== uid) {
    return { may: false, because: 'Only the claimant may change or withdraw the facts of her claim.' };
  }
  const role = s(k.role);
  if (OFFICER_ROLES.indexOf(role) !== -1 || SELF_FILER_ROLES.indexOf(role) === -1) {
    return { may: false, because: 'This claim was filed by an officer and does not identify its claimant as an account. '
      + 'The claimant changes her facts herself, once the claim is linked to her account.' };
  }
  const claimantEmail = s(c.claimantEmail).toLowerCase();
  if (claimantEmail && claimantEmail !== s(k.email).toLowerCase()) {
    return { may: false, because: 'This claim names a different claimant (' + s(c.claimantEmail) + '). '
      + 'Only she may change or withdraw its facts, once the claim is linked to her account.' };
  }
  return { may: true, because: '' };
}

// VALIDATION. input = { kind, field, text, reason, draftedFrom, edited } as the page sends it; claim = the claim as it
// stands. Returns { ok, problems, value }. It does not decide WHO - factsClaimant() does.
export function validateFactsAct(input, claim) {
  const i = input || {};
  const c = claim || {};
  const problems = [];
  const kind = s(i.kind);
  const field = s(i.field);
  const reason = s(i.reason);
  if (FACTS_ACT_KINDS.indexOf(kind) === -1) problems.push('The act must be one of: ' + FACTS_ACT_KINDS.join(', ') + '.');
  if (FACT_FIELDS.indexOf(field) === -1) problems.push('That is not one of the claimant’s facts.');
  if (reason.length > REASON_MAX) problems.push('The reason is longer than ' + REASON_MAX + ' characters.');
  if (s(c.status) === '' || s(c.status) === 'draft') {
    problems.push('A draft is still yours to edit directly; a change becomes a recorded act once the claim is filed.');
  }
  let text = '';
  if (!problems.length && kind === CHANGED) {
    text = typeof i.text === 'string' ? i.text.trim() : '';
    const cur = c[field];
    if (field !== 'statementOfClaim' && cur != null && typeof cur !== 'string') {
      problems.push('That part of the claim is a set of answers, not a single text; it cannot be changed through this act.');
    } else if (!text) {
      problems.push('A change needs the new text. To take a fact back, withdraw it.');
    } else if (text.length > TEXT_MAX) {
      problems.push('The new text is longer than ' + TEXT_MAX + ' characters.');
    } else if (text === factText(c, field) && !isWithdrawn(c, field)
               && !(field === 'statementOfClaim' && !s(c.statementOfClaim && c.statementOfClaim.approvedBy))) {
      problems.push('The new text is the same as the text on the claim. Nothing would change.');
    }
  }
  if (!problems.length && kind === WITHDRAWN) {
    const cur = c[field];
    const empty = cur == null || (typeof cur === 'string' && !cur.trim())
      || (field === 'statementOfClaim' && !factText(c, field))
      || (Array.isArray(cur) && cur.length === 0);
    if (empty) problems.push('There is nothing on the claim to withdraw there.');
    else if (isWithdrawn(c, field)) problems.push('That has already been withdrawn.');
  }
  return {
    ok: problems.length === 0,
    problems,
    value: {
      kind, field, text, reason,
      draftedFrom: s(i.draftedFrom).slice(0, 200),
      edited: i.edited === true,
    },
  };
}

// Firestore stores no `undefined`. A value read off the claim is copied as it is, with absent made null.
function kept(v) { return v === undefined ? null : v; }

function claimLabel(claim, claimId) {
  const c = claim || {};
  return s(c.claimNumber) || s(c.title) || s(claimId).slice(0, 8).toUpperCase();
}

// THE TWO NOTICES (ruling 138: "the organization ... must be informed about any such changes immediately").
// The shape is src\data\notifications.js base(): the Local Chairperson of the claim's scope (audience local_chair,
// scopeKey - the representative who carries it at the LC level) and the General Chairperson's office of the committee
// (audience general_chair, committee). Both recipientUid null: an office, not a person (ruling 60).
// Always two records. Where an address is blank the record is still written (the administrator reads every notice)
// and the gap is returned in `unaddressed` so the claimant is told, never left to assume.
export function factsNotices(input) {
  const i = input || {};
  const c = i.claim || {};
  const act = i.act || {};
  const label = claimLabel(c, i.claimId);
  const what = FIELD_LABELS[act.field] || act.field;
  const who = s(i.actorName) || s(c.claimantName) || 'The claimant';
  const day = s(i.at).slice(0, 10);
  const holder = s(c.carriedByName) || (s(c.carriedBy) === 'representative' ? 'a representative' : 'the claimant');
  const title = (act.kind === WITHDRAWN ? 'Facts withdrawn by the claimant: ' : 'Facts changed by the claimant: ') + label;
  const body = who + (act.kind === WITHDRAWN ? ' withdrew ' : ' changed ') + what + ' on claim ' + label
    + (day ? ' on ' + day : '') + '. '
    + (act.kind === WITHDRAWN
      ? 'The withdrawn text is kept on the claim, marked withdrawn. '
      : 'The earlier text is kept in the claim’s record of facts acts. ')
    + (s(act.reason) ? 'Her reason: ' + s(act.reason) + ' ' : '')
    + 'Carried by: ' + holder + '. The facts are hers to change or withdraw; the Organization is informed so it can '
    + 'protect the controlling agreement.';
  const common = {
    kind: NOTICE_KINDS[act.kind] || 'facts_changed',
    claimId: s(i.claimId),
    committee: s(c.committee),
    scopeKey: s(c.scopeKey),
    recipientUid: null,
    channel: 'in_app',
    title, body,
    actorName: who,
    actorRole: 'claimant',
    createdAt: s(i.at) || new Date(0).toISOString(),
    readAt: null,
  };
  const unaddressed = [];
  if (!common.scopeKey || /\|UNROUTED$/.test(common.scopeKey)) unaddressed.push('local_chair: the claim is not routed to a Local Chairperson');
  if (!common.committee) unaddressed.push('general_chair: the claim carries no committee');
  return {
    records: [
      Object.assign({}, common, { audience: 'local_chair' }),
      Object.assign({}, common, { audience: 'general_chair' }),
    ],
    unaddressed,
  };
}

// THE PLAN the function writes, in one transaction: the act, the claim patch, the two notices.
//   input  = validateFactsAct(...).value
//   caller = { uid, name }
//   at     = ISO time (the act's own `at` is replaced by the server timestamp when written)
// claimPatch keys are Firestore update paths ('factsWithdrawn.<field>'); `view` is the same change as plain fields,
// for the page to merge into the claim it holds.
export function planFactsAct({ claim, claimId, input, caller, actId, at }) {
  const c = claim || {};
  const v = input || {};
  const k = caller || {};
  const field = v.field;
  const before = kept(c[field]);
  let after;
  const claimPatch = {};
  const withdrawnMap = Object.assign({}, (c.factsWithdrawn && typeof c.factsWithdrawn === 'object') ? c.factsWithdrawn : {});

  if (v.kind === CHANGED) {
    if (field === 'statementOfClaim') {
      after = {
        text: v.text,
        approvedBy: s(k.uid),
        approvedByName: s(k.name),
        approvedAt: s(at),
        draftedFrom: v.draftedFrom || 'her own words',
        edited: v.edited === true,
        factsActId: s(actId),
      };
    } else {
      after = v.text;
    }
    claimPatch[field] = after;
    // A change re-asserts a withdrawn fact. The marker is set to null, not deleted: the withdrawal stays in the act record.
    if (withdrawnMap[field]) { claimPatch['factsWithdrawn.' + field] = null; withdrawnMap[field] = null; }
  } else {
    after = before;   // NEVER DESTRUCTIVE: the text is kept; only the marker is written
    const mark = { at: s(at), byUid: s(k.uid), reason: v.reason || '', factsActId: s(actId) };
    claimPatch['factsWithdrawn.' + field] = mark;
    withdrawnMap[field] = mark;
  }

  const act = {
    kind: v.kind,
    field,
    before,
    after: kept(after),
    at: s(at),
    byUid: s(k.uid),
    reason: v.reason || '',
  };
  const notices = factsNotices({ claim: c, claimId, act, actorName: s(k.name) || s(c.claimantName), at });
  const view = { factsWithdrawn: withdrawnMap };
  if (v.kind === CHANGED) view[field] = after;
  return { act, claimPatch, notices: notices.records, unaddressed: notices.unaddressed, view };
}

// The page's line for one act: "changed on DATE" / "withdrawn on DATE".
export function actLine(act) {
  const a = act || {};
  const at = a.at && typeof a.at.toDate === 'function' ? a.at.toDate().toISOString() : s(a.at);
  const what = FIELD_LABELS[a.field] || s(a.field);
  const verb = a.kind === WITHDRAWN ? 'withdrawn' : 'changed';
  return what.charAt(0).toUpperCase() + what.slice(1) + ' ' + verb + (at ? ' on ' + at.slice(0, 10) : '');
}

// The text a recorded before/after value shows as (the Statement of Claim carries its text inside an object).
export function valueText(v) {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'object' && typeof v.text === 'string') return v.text;
  try { return JSON.stringify(v); } catch (e) { return String(v); }
}
